import { z } from "zod";

// SOP Status Schema
export const SOPStatusSchema = z.enum(["active", "draft", "inactive"]);

// SOP DTO Schema for list view
export const SOPListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  purpose: z.string().nullable().optional(),
  scope: z.string().nullable().optional(),
  departmentId: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  responsiblePersonId: z.string().nullable().optional(),
  responsiblePersonName: z.string().nullable().optional(),
  status: SOPStatusSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  createdBy: z.string(),
  updatedBy: z.string(),
  // Position mappings for tags
  positionTags: z
    .array(
      z.object({
        positionId: z.string(),
        positionName: z.string(),
        departmentName: z.string().optional(),
      }),
    )
    .optional(),
});

// Full SOP DTO Schema with related data
export const SOPDetailSchema = SOPListItemSchema.extend({
  steps: z
    .array(
      z.object({
        id: z.string(),
        stepNumber: z.number(),
        title: z.string(),
        description: z.string().nullable().optional(),
        responsibleId: z.string().optional(),
        responsibleName: z.string().optional(),
        notes: z.string().optional(),
      }),
    )
    .optional(),
  documents: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        fileKey: z.string().nullable().optional(),
        fileUrl: z.string().optional(),
        fileName: z.string().optional(),
        fileType: z.string().optional(),
        createdAt: z.coerce.date(),
      }),
    )
    .optional(),
  warnings: z
    .array(
      z.object({
        id: z.string(),
        warningText: z.string(),
        createdAt: z.coerce.date(),
      }),
    )
    .optional(),
  positionTags: z
    .array(
      z.object({
        positionId: z.string(),
        positionName: z.string(),
        departmentName: z.string().optional(),
      }),
    )
    .optional(),
});

// SOP Base Schema
export const SOPBaseSchema = z.object({
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ SOP"),
  purpose: z.string().optional(),
  scope: z.string().optional(),
  departmentId: z.string().optional(),
  responsiblePersonId: z.string().optional(),
  status: SOPStatusSchema.default("draft"),
});

// Create SOP Schema
export const CreateSOPSchema = SOPBaseSchema;

// Update SOP Schema
export const UpdateSOPSchema = SOPBaseSchema.partial();

// SOP Step Schema
export const SOPStepSchema = z.object({
  sopId: z.string(),
  stepNumber: z.number().int().min(1),
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຂັ້ນຕອນ"),
  description: z.string().optional(),
  responsibleId: z.string().optional(),
  notes: z.string().optional(),
});

// Create SOP Step Schema
export const CreateSOPStepSchema = SOPStepSchema.omit({ sopId: true });

// Update SOP Step Schema
export const UpdateSOPStepSchema = SOPStepSchema.partial();

// SOP Document Schema
export const SOPDocumentSchema = z.object({
  sopId: z.string(),
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ເອກະສານ"),
  fileUrl: z.string().url(),
  fileType: z.string().optional(),
});

// Create SOP Document Schema
export const CreateSOPDocumentSchema = SOPDocumentSchema.omit({ sopId: true });

// Update SOP Document Schema
export const UpdateSOPDocumentSchema = SOPDocumentSchema.partial();

// SOP Warning Schema
export const SOPWarningSchema = z.object({
  sopId: z.string(),
  warningText: z.string().min(1, "ກະລຸນາໃສ່ຂໍ້ຄວນລະວັງ"),
});

// Create SOP Warning Schema
export const CreateSOPWarningSchema = SOPWarningSchema.omit({ sopId: true });

// Update SOP Warning Schema
export const UpdateSOPWarningSchema = SOPWarningSchema.partial();

// SOP Position Mapping Schema
export const SOPPositionMappingSchema = z.object({
  sopId: z.string(),
  positionId: z.string(),
  departmentId: z.string().optional(),
});

// Create SOP Position Mapping Schema
export const CreateSOPPositionMappingSchema = SOPPositionMappingSchema.omit({
  sopId: true,
});

// Update SOP Position Mapping Schema
export const UpdateSOPPositionMappingSchema =
  SOPPositionMappingSchema.partial();

// Query Parameters
export const SOPQuerySchema = z.object({
  offset: z.number().int().min(0).default(0),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  departmentId: z.string().optional(),
  status: SOPStatusSchema.optional(),
  sort: z.string().optional(),
});

// Form Step Schema (for form input)
export const SOPFormStepSchema = z.object({
  id: z.string(), // nanoid for new items, actual id for existing
  stepNumber: z.number().int().min(1),
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ຂັ້ນຕອນ"),
  description: z.string().nullable().optional(),
  responsibleId: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

// Form Document Schema (for form input)
export const SOPFormDocumentSchema = z.object({
  id: z.string(), // nanoid for new items, actual id for existing
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ເອກະສານ"),
  fileKey: z.string().nullable().optional(),
  file: z.instanceof(File).optional(), // new file upload
});

// FormData specific schemas
export const CreateSOPFormSchema = z.object({
  title: z.string().min(1, "ກະລຸນາໃສ່ຊື່ SOP"),
  purpose: z.string().nullable().optional(),
  scope: z.string().nullable().optional(),
  departmentId: z.string().nullable().optional(),
  responsiblePersonId: z.string().nullable().optional(),
  positionIds: z.array(z.string()).default([]), // multi-select positions
  steps: z.array(SOPFormStepSchema).default([]),
  documents: z.array(SOPFormDocumentSchema).default([]),
  warnings: z.array(z.string()).default([]), // array of warning texts
  status: SOPStatusSchema.default("draft"),
});

export const UpdateSOPFormSchema = CreateSOPFormSchema.partial();

// Export types
export type SOPListItemDTO = z.infer<typeof SOPListItemSchema>;
export type SOPDetailDTO = z.infer<typeof SOPDetailSchema>;
export type CreateSOPDTO = z.infer<typeof CreateSOPSchema>;
export type UpdateSOPDTO = z.infer<typeof UpdateSOPSchema>;
export type CreateSOPStepDTO = z.infer<typeof CreateSOPStepSchema>;
export type UpdateSOPStepDTO = z.infer<typeof UpdateSOPStepSchema>;
export type CreateSOPDocumentDTO = z.infer<typeof CreateSOPDocumentSchema>;
export type UpdateSOPDocumentDTO = z.infer<typeof UpdateSOPDocumentSchema>;
export type CreateSOPWarningDTO = z.infer<typeof CreateSOPWarningSchema>;
export type UpdateSOPWarningDTO = z.infer<typeof UpdateSOPWarningSchema>;
export type CreateSOPPositionMappingDTO = z.infer<
  typeof CreateSOPPositionMappingSchema
>;
export type UpdateSOPPositionMappingDTO = z.infer<
  typeof UpdateSOPPositionMappingSchema
>;
export type SOPQueryDTO = z.infer<typeof SOPQuerySchema>;
export type SOPFormStepDTO = z.infer<typeof SOPFormStepSchema>;
export type SOPFormDocumentDTO = z.infer<typeof SOPFormDocumentSchema>;
export type CreateSOPFormDTO = z.infer<typeof CreateSOPFormSchema>;
export type UpdateSOPFormDTO = z.infer<typeof UpdateSOPFormSchema>;

// ID Parameters
export const IdParamSchema = z.object({ id: z.string().min(1) });
export type IdParamDTO = z.infer<typeof IdParamSchema>;
